import {
  type SupplierListItem,
  type SupplierTeamMember,
  type SupplierUserRole,
} from '@choco-admin/types';
import { getInitials } from '@choco-admin/utils';
import {
  Card,
  CardBody,
  CardHeader,
  Button,
  Table,
  TableHeader,
  TableColumn,
  TableBody,
  TableRow,
  TableCell,
  Avatar,
  Chip,
  Spinner,
} from '@heroui/react';
import React, { useCallback, useState } from 'react';
import { FiUsers, FiMail, FiPhone } from 'react-icons/fi';

import { AlertCard } from '../../../../../../components/AlertCard';
import { SupplierRoleSelect } from '../../../../../../components/SupplierRoleSelect';

import { useSupplierTeam } from './hooks/useSupplierTeam';
import { useSupplierTeamActions } from './hooks/useSupplierTeamActions';
import { RemoveSupplierMemberModal } from './RemoveSupplierMemberModal';
import { SupplierJoinRequests } from './SupplierJoinRequests';

interface TeamTabProps {
  supplier?: SupplierListItem;
}

export const TeamTab: React.FC<TeamTabProps> = ({ supplier }) => {
  const supplierId = supplier?.id;
  const { teamMembers, isTruncated, isLoading, error, refetch } =
    useSupplierTeam(supplierId);
  const { updateMemberRole, removeMember, isUpdatingRole, isRemoving } =
    useSupplierTeamActions({ onRefetch: refetch });

  const [memberToRemove, setMemberToRemove] = useState<{
    id: string;
    name: string;
  } | null>(null);

  const handleRoleChange = useCallback(
    async (userId: string, role: SupplierUserRole) => {
      try {
        await updateMemberRole(userId, role);
      } catch {
        // Error notification handled in the hook.
      }
    },
    [updateMemberRole]
  );

  const confirmRemove = useCallback(async () => {
    if (!memberToRemove) return;
    try {
      await removeMember(memberToRemove.id);
      setMemberToRemove(null);
    } catch {
      // Error notification handled in the hook.
    }
  }, [memberToRemove, removeMember]);

  const renderTeamBody = (): React.ReactNode => {
    if (isLoading && teamMembers.length === 0) {
      return (
        <div className="flex justify-center items-center py-8">
          <Spinner size="lg" />
        </div>
      );
    }

    // Only surface the error state when there's nothing to show — a transient
    // background refetch failure shouldn't blank an already-populated list.
    if (error && teamMembers.length === 0) {
      return (
        <AlertCard
          type="error"
          title="Error loading team members"
          message={error.message}
        />
      );
    }

    if (teamMembers.length === 0) {
      return (
        <div className="text-center py-8 text-default-500">
          <FiUsers size={48} className="mx-auto mb-4 opacity-50" />
          <p>No team members found for this distributor.</p>
          <p className="text-sm mt-2">
            Members are added by linking a user to this distributor from the
            user&apos;s profile.
          </p>
        </div>
      );
    }

    return (
      <Table aria-label="Distributor team members table">
        <TableHeader>
          <TableColumn>Member</TableColumn>
          <TableColumn>Contact</TableColumn>
          <TableColumn>Role</TableColumn>
          <TableColumn>Actions</TableColumn>
        </TableHeader>
        <TableBody>
          {teamMembers.map((member: SupplierTeamMember) => (
            <TableRow key={member.id}>
              <TableCell>
                <div className="flex items-center gap-3">
                  <Avatar
                    src={member.profileImage?.url || undefined}
                    name={getInitials(member.name || 'U')}
                    size="md"
                    color="primary"
                  />
                  <div>
                    <p className="font-semibold">
                      {member.name || 'Unknown Member'}
                    </p>
                    <p className="text-sm text-default-500">Team Member</p>
                  </div>
                </div>
              </TableCell>
              <TableCell>
                <div className="text-sm">
                  {member.email && (
                    <div className="flex items-center gap-1">
                      <FiMail size={12} />
                      {member.email}
                    </div>
                  )}
                  {member.phone && (
                    <div className="flex items-center gap-1 mt-1">
                      <FiPhone size={12} />
                      {member.phone}
                    </div>
                  )}
                </div>
              </TableCell>
              <TableCell>
                <SupplierRoleSelect
                  value={member.role}
                  onChange={(role) => handleRoleChange(member.id, role)}
                  disabled={isUpdatingRole || isRemoving}
                />
              </TableCell>
              <TableCell>
                <Button
                  size="sm"
                  variant="light"
                  color="danger"
                  onPress={() =>
                    setMemberToRemove({
                      id: member.id,
                      name: member.name || 'Unknown Member',
                    })
                  }
                  isDisabled={isRemoving}
                >
                  Remove
                </Button>
              </TableCell>
            </TableRow>
          ))}
        </TableBody>
      </Table>
    );
  };

  if (!supplier) {
    return (
      <AlertCard
        type="info"
        title="Create distributor first"
        message="Please save the distributor to manage its team."
      />
    );
  }

  return (
    <div className="max-w-4xl mx-auto space-y-6">
      <Card>
        <CardHeader className="flex flex-row items-center justify-between">
          <div className="flex items-center gap-3">
            <h3 className="text-lg font-semibold">Team Members</h3>
            {teamMembers.length > 0 && (
              <Chip size="sm" variant="flat" color="primary">
                {teamMembers.length}
                {isTruncated ? '+' : ''} members
              </Chip>
            )}
          </div>
        </CardHeader>
        <CardBody>
          {renderTeamBody()}
          {isTruncated && (
            <p className="mt-3 text-sm text-default-500">
              Showing the first {teamMembers.length} members. This distributor
              has more team members than are displayed here.
            </p>
          )}
        </CardBody>
      </Card>

      {/* Below the member list on purpose: the team is what this tab is for,
          and a request to join is the rare case. Rendered independently of the
          member query, so a failing team read cannot take approve and decline
          away from the reader. */}
      <SupplierJoinRequests supplierId={supplier.id} onTeamChanged={refetch} />

      <RemoveSupplierMemberModal
        isOpen={memberToRemove !== null}
        onClose={() => setMemberToRemove(null)}
        onConfirm={confirmRemove}
        memberName={memberToRemove?.name || ''}
        isRemoving={isRemoving}
      />
    </div>
  );
};
